import React from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, ChevronRight } from "lucide-react";
import { JOURNEY_GROUPS, computeGroupState, activeGroupKey } from "@/lib/journeyGroups";

/**
 * CUST-EXP-001A · JourneyRail — visual 8-step journey ribbon derived
 * deterministically from canonical `project.phases`. No new workflow
 * state is created; every group maps to canonical `phase_key`s via
 * `lib/journeyGroups.js`. The whole ribbon deep-links to /journey.
 */
export default function JourneyRail({ project }) {
    const active = activeGroupKey(project);
    return (
        <div className="pe-card p-5 md:p-6" data-testid="journey-rail">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="text-[11px] tracking-[0.14em] text-[#4B5565] uppercase">Your Installation Journey</div>
                    <div className="text-lg text-[#131B2F] mt-0.5" data-testid="journey-rail-progress">
                        {project?.progress ?? 0}% complete
                    </div>
                </div>
                <Link
                    to="/journey"
                    data-testid="journey-rail-view"
                    className="text-sm text-[#3E9E2E] hover:text-[#2E7E22] font-medium inline-flex items-center gap-1"
                    aria-label="Open your full installation journey"
                >
                    See all phases <ChevronRight size={14} />
                </Link>
            </div>

            <ol className="grid grid-cols-4 md:grid-cols-8 gap-2" data-testid="journey-rail-groups">
                {JOURNEY_GROUPS.map((group, idx) => {
                    const s = computeGroupState(project, group);
                    const isActive = group.key === active;
                    const done = s.status === "complete";
                    return (
                        <li
                            key={group.key}
                            data-testid={`journey-group-${group.key}`}
                            data-state={s.status}
                            className="relative flex flex-col items-center gap-2"
                        >
                            <div
                                className={`w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-medium transition-colors ${
                                    done
                                        ? "bg-[#3E9E2E] text-white"
                                        : isActive
                                            ? "bg-[#F26522]/15 text-[#B45309] ring-2 ring-[#F26522]/40"
                                            : "bg-[#F1F8EC] text-[#4B5565]"
                                }`}
                                aria-hidden="true"
                            >
                                {done
                                    ? <Check size={14} strokeWidth={2.5} />
                                    : isActive
                                        ? <Loader2 size={12} strokeWidth={2} className="animate-spin motion-reduce:animate-none" />
                                        : idx + 1}
                            </div>
                            <div className={`text-[10px] md:text-xs text-center leading-tight ${
                                done || isActive ? "text-[#131B2F] font-medium" : "text-[#4B5565]"
                            }`}>
                                {group.label}
                            </div>
                            {/* Segment fill under the label */}
                            <div className="w-full h-1 rounded-full bg-[#E5E7EB] overflow-hidden">
                                <div
                                    className="h-full transition-all duration-500 motion-reduce:transition-none"
                                    style={{
                                        width: `${s.percent}%`,
                                        background: done ? "#3E9E2E" : isActive ? "#F26522" : "#D1D5DB",
                                    }}
                                />
                            </div>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}
